import { Heading } from '@boltenergy-be/design-system'
import DashboardCard from '../components/dashboard-card/DashboardCard.tsx'
import { useMemo } from 'react'
import dayjs from 'dayjs'
import { isReportEmpty, mapAgentBreakdownToSalesTableEntry } from './utils.ts'
import { ExportBillingDetailsReportQueryStringParameters } from '@boltenergy-be/shared'
import { popErrorToast } from '@boltenergy-be/design-system'
import { downloadFile } from 'utils/files.ts'
import { log } from 'utils/logging.ts'
import { Flow } from 'types/logging.ts'
import NestedDataTable from '../components/nested-data-table/NestedDataTable.tsx'
import { RATE_TABLE_COLUMNS } from './constants.ts'
import { useGetBillingDetailsReportsQuery, useLazyGetBillingDetailsReportExportQuery } from 'store/queries/bolt-backend/reports'

const BillingDetailsReports = () => {
  // Constants
  const today = useMemo(() => dayjs(), [])
  const from = today.subtract(7, 'months').startOf('month')

  // Redux queries
  const { currentData, isFetching } = useGetBillingDetailsReportsQuery({
    from: from.format('YYYY-MM'),
    to: today.format('YYYY-MM')
  })
  const [triggerExport] = useLazyGetBillingDetailsReportExportQuery()

  // Datasets
  const domiciliationData = useMemo(
    () => mapAgentBreakdownToSalesTableEntry(currentData?.domiciliationRate, 'rate'),
    [currentData?.domiciliationRate]
  )
  const eBillingData = useMemo(() => mapAgentBreakdownToSalesTableEntry(currentData?.eBillingRate, 'rate'), [currentData?.eBillingRate])

  /**
   * Export report to CSV through endpoint
   * @param {ExportBillingDetailsReportQueryStringParameters["report"]} report
   * @returns {Promise<void>}
   */
  const handleExport = async (report: ExportBillingDetailsReportQueryStringParameters['report']) => {
    try {
      const csv = await triggerExport({ report, from: from.format('YYYY-MM'), to: today.format('YYYY-MM') }).unwrap()
      downloadFile(csv, `${report}-${today.format('YYYY-MM-DD')}.csv`, 'text/csv;charset=utf-8;')
    } catch (err) {
      popErrorToast('Something went wrong while exporting the report')
      log({ error: err as Error, identifier: `[${Flow.REPORTS}:handleExport]` })
    }
  }

  return (
    <>
      <Heading as="h2" variant="h4">
        Service contracts: extra information
      </Heading>

      <DashboardCard
        variant="large"
        title="Domiciliation rate"
        description="% that has domiciliation"
        loading={isFetching}
        handleExport={async () => await handleExport('domiciliationRate')}
        disableExport={isReportEmpty(currentData?.domiciliationRate)}
      >
        <NestedDataTable columns={RATE_TABLE_COLUMNS} rows={domiciliationData} />
      </DashboardCard>

      <DashboardCard
        variant="large"
        title="E-billing rate"
        description="% that has e-billing"
        loading={isFetching}
        handleExport={async () => await handleExport('eBillingRate')}
        disableExport={isReportEmpty(currentData?.eBillingRate)}
      >
        <NestedDataTable columns={RATE_TABLE_COLUMNS} rows={eBillingData} />
      </DashboardCard>
    </>
  )
}

export default BillingDetailsReports
